import React, { Fragment, ReactElement } from 'react';
import classnames from 'classnames';
import { bindActionCreators, Dispatch } from 'redux';
import { connect, useSelector } from 'react-redux';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';

// Components

import Form from '@lib/wrappers/Form';
import NamesOnSsnCardInputs from './pageIncludes/common/names/NamesOnSsnCardInputs';
import PageHeader from '../headers/PageHeader';
import UniversalInput from '@lib/wrappers/UniversalInput';
import ValidationErrorMessage from '@components/ssap/notifications/ValidationErrorMessage';

// ActionCreators

import { changeData } from '@actions/ssap/ssapActionCreators';
import { selectExchangeAndProgramNames } from '@reducers/appReducer';

// Utils

import { cloneObject } from '@utils/miscellaneous/cloneObject';
import { normalizeNameOfPerson } from '@utils/normalize/normalizeNameOfPerson';
import { preventerTools } from '@utils/tools/preventerTools';

// Constants

import { DROPDOWN_OPTIONS } from '@constants/ssap/hhmTypes/dropdownOptions';
import { EMPTY_OBJECTS } from '@constants/ssap/initialValues/emptyObjects';
import { GENDER_TYPES } from '@constants/ssap/miscellaneous/miscellaneous';
import { INPUT_TYPES } from '@constants/inputs/inputTypes';
import { VALIDATION_ERROR_MESSAGES } from '@constants/inputs/validationErrorMessages';
import { VALIDATION_TYPES } from '@constants/inputs/validationTypes';

// Types

import {
    ApplicationStore,
    HouseholdMember,
    OnChangeArgTypes,
    SsapPage,
    SsapReduxActions
} from '@components/ssap/SsapTypes';


/**
 *
 * @param {Object} props
 * @returns {React.Component} - 'Gender and SSN information' Page
 *
 */
const Page2_08 = (props: SsapPage): ReactElement => {

    const {
        data,
        environment: {
            isState
        },
        form,
        householdMembers
    } = props;
    
    const intlApi: IntlShape = useIntl();

    const {
        currentHhm: {
            applyingForCoverageIndicator,
            gender,
            name,
            socialSecurityCard: {
                firstNameOnSSNCard,
                lastNameOnSSNCard,
                middleNameOnSSNCard,
                nameSameOnSSNCardIndicator,
                noSsnIssuedAttestation,
                socialSecurityNumber,
                suffixOnSSNCard
            }
        },
        formName,
        hhmIndex: member,
        pageName
    } = props;
    
    const currentData = householdMembers[member];

    const {
        isFormSubmitted,
        isFormOnTopLevelValid
    }: {
        isFormSubmitted: boolean,
        isFormOnTopLevelValid: boolean
    } = preventerTools.provideObject(form[formName]);

    const personName: string = normalizeNameOfPerson(name);

    const onFieldChange_gender = (newData: HouseholdMember): void => {
        const newState = { ...data };
        let dataForSave = { ...newData };

        if(newData.gender === GENDER_TYPES.MALE) {
            const {
                dueDateOfPregnancy,
                numberBabiesExpectedInPregnancy,
                pregnantIndicator
            } = cloneObject(EMPTY_OBJECTS.HOUSEHOLD_MEMBER.specialCircumstances);

            dataForSave = {
                ...dataForSave,
                specialCircumstances: {
                    ...dataForSave.specialCircumstances,
                    dueDateOfPregnancy,
                    numberBabiesExpectedInPregnancy,
                    pregnantIndicator
                }
            };
        }

        newState.taxHousehold.householdMember[member] = dataForSave;
        props.actions.changeData(newState);
    };

    const onFieldChange = (...args: OnChangeArgTypes): void => {

        const [ newData, , , [ name, value ] ] = args;

        const {
            name: {
                firstName,
                middleName,
                lastName,
                suffix
            }
        } = householdMembers[member];
            
        if(name === 'nameSameOnSSNCardIndicator' && value) {
            newData.socialSecurityCard = {
                ...newData.socialSecurityCard,
                firstNameOnSSNCard: firstName,
                middleNameOnSSNCard: middleName,
                lastNameOnSSNCard: lastName,
                suffixOnSSNCard: suffix,
            };
        }

        if(name === 'nameSameOnSSNCardIndicator' && !value) {
            newData.socialSecurityCard = {
                ...newData.socialSecurityCard,
                firstNameOnSSNCard: null,
                middleNameOnSSNCard: null,
                lastNameOnSSNCard: null,
                suffixOnSSNCard: null,
            };
        }

        const newState = { ...data };

        newState.taxHousehold.householdMember[member] = newData;
        props.actions.changeData(newState);
    };

    const handleSsnChange = (...args: OnChangeArgTypes): void => {

        const [ newData, , , [ , value ] ] = args;

        const newState = { ...data };

        if(value === null) {
            newState
                .taxHousehold
                .householdMember[member]
                .socialSecurityCard = {
                    ...cloneObject(EMPTY_OBJECTS.SOCIAL_SECURITY_CARD),
                    noSsnIssuedAttestation: false
                };
        } else {
            newState
                .taxHousehold
                .householdMember[member]
                .socialSecurityCard = {
                    ...newState
                        .taxHousehold
                        .householdMember[member]
                        .socialSecurityCard,
                    socialSecurityNumber: newData.socialSecurityCard.socialSecurityNumber,
                    socialSecurityCardHolderIndicator: true,
                    noSsnIssuedAttestation: false
                };
        }
        props.actions.changeData(newState);
    };

    const getValuesToMatch: Set<any> = (() => {
        return new Set(
            householdMembers
                .map((hhm: HouseholdMember) => hhm.socialSecurityCard.socialSecurityNumber)
                .filter((ssn: string | null, idx: number) => idx !== member && ssn)
        );
    })();


    const checkTopLevelValidity = (): boolean => {
        if(applyingForCoverageIndicator) {
            return !!socialSecurityNumber || !!noSsnIssuedAttestation;
        }

        return true;
    };

    const requiredForApplyingClassName = classnames(
        { 'required-true': applyingForCoverageIndicator }
    );
    const { exchangeMedicaid } = useSelector(selectExchangeAndProgramNames);
    return (
        <Fragment>
            <div
                id = 'fhGetReady'
                className = 'fade-in'
            >
                <Form
                    { ...{
                        formName,
                        pageName,
                        topLevelValidity: checkTopLevelValidity()
                    } }
                >

                    <div className = 'subsection' >
                        <UniversalInput
                            legend = { {
                                ...(isState.NJ && { 
                                    dataInstructions: 'instructional_text.sex_assigned_at_birth_information',
                                    replacements: {
                                        exchangeMedicaid : exchangeMedicaid,
                                    },
                                }),
                                legendId: `${pageName}_${member}_person_gender`,
                                legendText: 'ssap.input.legend.person_gender',
                                legendTextReplacements: {
                                    personName: (
                                        <span>
                                            <strong>
                                                { personName }
                                            </strong>
                                        </span>
                                    )
                                }
                            } }
                            label = { {
                                showRequiredIcon: true
                            } }
                            inputData = { {
                                isInputRequired: true,
                                type: INPUT_TYPES.RADIO_BUTTONS,
                                name: 'gender',
                                value: gender,
                                options: DROPDOWN_OPTIONS.GENDER,
                                validationType: VALIDATION_TYPES.CHOSEN,
                                currentData: currentData,
                                fields: []
                            } }
                            inputActions = { {
                                onFieldChange: onFieldChange_gender
                            } }
                            errorClasses = { [ 'margin-l-0' ] }
                        />
                    </div>
                    
                    <div className = 'subsection' >

                        <div className = { requiredForApplyingClassName } >
                            <FormattedMessage
                                id = 'ssap.family_household.personal_info.enter_ssn'
                                values = { {
                                    personName: (
                                        <strong>
                                            { personName }
                                        </strong>
                                    )
                                } }
                            />
                        </div>

                        <ValidationErrorMessage
                            isVisible = { isFormSubmitted && !isFormOnTopLevelValid }
                            messageText = { VALIDATION_ERROR_MESSAGES.ANY_VALUE }
                            errorClasses = { [ 'margin-l-0', 'margin-b-10' ] }
                        />
 
                        <UniversalInput
                            label = { {
                                text: 'ssap.input.label.ssn'
                            } }
                            inputData = { {
                                isInputRequired: false,
                                type: INPUT_TYPES.SSN_INPUT,
                                name: 'socialSecurityNumber',
                                helperClasses: [],
                                value: socialSecurityNumber,
                                placeholder: 'ssap.input.placeholder.ssn',
                                validationType: VALIDATION_TYPES.SSN,
                                valuesToMatch: getValuesToMatch,
                                currentData: currentData,
                                fields: [ 'socialSecurityCard' ],
                                isInputStandalone: false
                            } }
                            inputActions = { {
                                onFieldChange: handleSsnChange
                            } }
                            indexInArray = { member }
                        />

                        {
                            applyingForCoverageIndicator &&

                            <UniversalInput
                                label = { {
                                    dataInstructions: 'instructional_text.no_ssn_issued',
                                    instructionsViaPortal: [ true ],
                                    labelTextReplacements: {
                                        personName: (
                                            <strong>
                                                { personName }
                                            </strong>
                                        )
                                    },
                                    text: 'ssap.input.label.no_ssn_issued'
                                } }
                                inputData = { {
                                    isInputRequired: false,
                                    disabled: !!socialSecurityNumber,
                                    type: INPUT_TYPES.CHECKBOX,
                                    name: 'noSsnIssuedAttestation',
                                    classes : [ '' ],
                                    checked: noSsnIssuedAttestation,
                                    value: noSsnIssuedAttestation,
                                    currentData: currentData,
                                    fields: [ 'socialSecurityCard' ],
                                    isInputStandalone: true
                                } }
                                inputActions = { {
                                    onFieldChange
                                } }
                            />
                        }

                        <div className = 'usa-alert usa-alert--info gi-alert gi-alert__info' >
                            <div className = 'usa-alert__body gi-alert__info__body' >
                                <p className = 'usa-alert__text gi-alert__info__text' >
                                    <FormattedMessage
                                        id = {
                                            applyingForCoverageIndicator
                                                ? 'ssap.family_household.personal_info.ssn_warning_who_applies'
                                                : 'ssap.family_household.personal_info.ssn_warning_who_doesnt_apply'
                                        }
                                        values = { {
                                            break: <Fragment><br/><br/></Fragment>,
                                            personName: (
                                                <strong>
                                                    { personName }
                                                </strong>
                                            ),
                                            link: (
                                                <a
                                                    className = 'usa-link--external'
                                                    href = {
                                                        intlApi.formatMessage({
                                                            id: 'ssap.family_household.personal_info.ssa_link'
                                                        })
                                                    }
                                                    target = '_blank'
                                                    rel = 'noopener noreferrer'
                                                >
                                                    <FormattedMessage
                                                        id = 'common.non_internationalized_text'
                                                        values = { {
                                                            text: intlApi.formatMessage({
                                                                id: 'ssap.family_household.personal_info.ssa_link_text'
                                                            })
                                                        } }
                                                    />
                                                </a>
                                            )
                                        } }
                                    />
                                </p>
                            </div>
                        </div>

                        {
                            socialSecurityNumber &&

                                <UniversalInput
                                    legend = { {
                                        legendText: 'ssap.input.legend.same_name_on_ssc',
                                        legendTextReplacements: {
                                            personName: (
                                                <strong>
                                                    { personName }
                                                </strong>
                                            ),
                                            gender
                                        }
                                    } }
                                    label = { {
                                        labelClass: [ 'margin-t-20' ],
                                        showRequiredIcon: true
                                    } }
                                    inputData = { {
                                        isInputRequired: true,
                                        type: INPUT_TYPES.RADIO_BUTTONS,
                                        name: 'nameSameOnSSNCardIndicator',
                                        value: nameSameOnSSNCardIndicator,
                                        options: DROPDOWN_OPTIONS.YES_NO,
                                        validationType: VALIDATION_TYPES.CHOSEN,
                                        currentData: currentData,
                                        fields: [ 'socialSecurityCard' ],
                                        isInputStandalone: true
                                    } }
                                    inputActions = { {
                                        onFieldChange
                                    } }
                                />
                        }

                        {
                            nameSameOnSSNCardIndicator === false &&

                                <Fragment>
                                    <PageHeader
                                        text = 'ssap.subheader.same_name_as_on_ssn_card'
                                        textReplacements = { {
                                            personName
                                        } }
                                        headerClassName = 'gi-heading-alt margin-t-20'
                                    />
                                    <NamesOnSsnCardInputs
                                        {...{
                                            firstNameOnSSNCard,
                                            middleNameOnSSNCard,
                                            lastNameOnSSNCard,
                                            suffixOnSSNCard,
                                            currentData,
                                            inputActions: {
                                                onFieldChange
                                            },
                                            underFormControl: true,
                                            isInputStandalone: true
                                        } }
                                    />
                                </Fragment>       
                        }
                    </div>
                </Form>
            </div>
        </Fragment>
    );
};

const mapStateToProps = (state: ApplicationStore): Partial<SsapPage> => ({
    environment: state.common.environment,
    data: state.ssap.data,
    form: state.ssap.form,

    householdMembers: state
        .ssap
        .data
        .taxHousehold
        .householdMember
});

const mapDispatchToProps = (dispatch: Dispatch): { actions: Partial<SsapReduxActions> } => ({
    actions: bindActionCreators({
        changeData
    }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(Page2_08);
